if (!customElements.get("sw-navigation-header")) {
  customElements.define(
    "sw-navigation-header",
    class extends HTMLElement {
      connectedCallback() {
        if (this.dataset.swReady === "true") return;
        this.dataset.swReady = "true";

        this.mobileMenu = this.querySelector("[data-sw-mobile-menu]");
        this.openButton = this.querySelector("[data-sw-menu-open]");
        this.cartLink = this.querySelector("[data-sw-cart-link]");
        this.desktopDetails = this.querySelectorAll(
          "[data-sw-desktop-details]",
        );
        this.desktopOpenTimers = new WeakMap();
        this.desktopCloseTimers = new WeakMap();
        this.desktopHoverQuery = window.matchMedia(
          "(min-width: 990px) and (hover: hover) and (pointer: fine)",
        );
        this.isDesktopViewport = window.innerWidth >= 990;
        this.searchDetails = this.querySelector("[data-sw-search-details]");
        this.searchForm = this.querySelector("[data-sw-search-form]");
        this.searchInput = this.querySelector("[data-sw-search-input]");
        this.searchClear = this.querySelector("[data-sw-search-clear]");
        this.searchResults = this.querySelector("[data-sw-search-results]");
        this.searchDefault = this.querySelector("[data-sw-search-default]");
        this.searchShortcut = this.querySelector("[data-sw-search-shortcut]");
        this.deviceSearch = window.SlickwrapsDeviceSearch;
        this.searchRequestId = 0;
        this.searchController = new AbortController();
        this.lastFocused = null;

        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onResize = this.onResize.bind(this);
        this.onCartCountUpdate = this.onCartCountUpdate.bind(this);

        this.openButton?.addEventListener("click", () => {
          if (this.mobileMenu && !this.mobileMenu.hidden) {
            this.closeMobileMenu();
          } else {
            this.openMobileMenu();
          }
        });

        this.initializeDeviceSearch();
        this.initializeMobileMenuControls();
        this.initializeDeviceCategoryControls();

        this.desktopDetails.forEach((details) => {
          const summary = details.querySelector(":scope > summary");
          details.dataset.swDesktopClickArmed = "false";
          summary?.setAttribute(
            "aria-expanded",
            details.open ? "true" : "false",
          );

          summary?.addEventListener("click", (event) => {
            this.hydrateDesktopMenu(details);
            const destination = details.dataset.swDesktopDestination;
            if (window.innerWidth < 990 || !destination) {
              return;
            }

            event.preventDefault();

            if (
              details.open &&
              details.dataset.swDesktopClickArmed === "true"
            ) {
              window.location.assign(destination);
              return;
            }

            this.openDesktopMenu(details);
            details.dataset.swDesktopClickArmed = "true";
          });

          const hoverTarget =
            details.closest("[data-sw-device-desktop-trigger]") || details;

          hoverTarget.addEventListener("pointerenter", () => {
            if (details.hasAttribute("data-sw-search-details")) return;
            if (!this.desktopHoverQuery.matches) return;
            this.clearDesktopClose(details);
            this.queueDesktopOpen(details);
          });

          hoverTarget.addEventListener("pointerleave", () => {
            if (details.hasAttribute("data-sw-search-details")) return;
            if (!this.desktopHoverQuery.matches) return;
            this.clearDesktopOpen(details);
            this.queueDesktopClose(details);
          });

          details.addEventListener("focusin", () => {
            if (window.innerWidth < 990) return;
            this.clearDesktopOpen(details);
            this.clearDesktopClose(details);
          });

          details.addEventListener("focusout", (event) => {
            if (
              window.innerWidth >= 990 &&
              !details.contains(event.relatedTarget)
            ) {
              this.clearDesktopOpen(details);
              this.queueDesktopClose(details);
            }
          });

          details.addEventListener("toggle", () => {
            if (details.open) this.hydrateDesktopMenu(details);
            summary?.setAttribute(
              "aria-expanded",
              details.open ? "true" : "false",
            );
            if (details.open) {
              this.desktopDetails.forEach((other) => {
                if (other !== details) other.removeAttribute("open");
              });
              if (details.hasAttribute("data-sw-search-details")) {
                window.requestAnimationFrame(() => {
                  details.querySelector("[data-sw-search-input]")?.focus();
                });
              }
            } else {
              details.dataset.swDesktopClickArmed = "false";
              if (details.hasAttribute("data-sw-search-details")) {
                this.resetDeviceSearch();
              }
            }
          });
        });

        document.addEventListener("click", this.onDocumentClick);
        document.addEventListener("keydown", this.onKeydown);
        document.addEventListener("cart:count:update", this.onCartCountUpdate);
        window.addEventListener("resize", this.onResize, { passive: true });

        if (window.Shopify?.designMode) {
          this.onSectionUnload = (event) => {
            if (event.target.contains(this)) this.closeMobileMenu(false);
          };
          document.addEventListener(
            "shopify:section:unload",
            this.onSectionUnload,
          );
        }
      }

      disconnectedCallback() {
        document.removeEventListener("click", this.onDocumentClick);
        document.removeEventListener("keydown", this.onKeydown);
        document.removeEventListener(
          "cart:count:update",
          this.onCartCountUpdate,
        );
        window.removeEventListener("resize", this.onResize);
        this.searchRequestId += 1;
        this.searchController?.abort();
        if (this.onSectionUnload) {
          document.removeEventListener(
            "shopify:section:unload",
            this.onSectionUnload,
          );
        }
      }

      onDocumentClick(event) {
        if (!this.contains(event.target)) {
          this.closeDesktopMenus();
        }
      }

      onKeydown(event) {
        if (this.mobileMenu && !this.mobileMenu.hidden) {
          if (event.key === "Escape") {
            this.closeMobileMenu();
          } else if (event.key === "Tab") {
            this.trapMobileFocus(event);
          }
          return;
        }

        if (event.key === "Escape") this.closeDesktopMenus();
      }

      onResize() {
        const isDesktopViewport = window.innerWidth >= 990;

        // Mobile browsers resize the visual viewport while their address bar or
        // keyboard moves. Only reset navigation when the layout breakpoint changes.
        if (isDesktopViewport === this.isDesktopViewport) return;

        this.isDesktopViewport = isDesktopViewport;
        this.closeDesktopMenus();

        if (
          isDesktopViewport &&
          this.mobileMenu &&
          !this.mobileMenu.hidden
        ) {
          this.closeMobileMenu(false);
        }
      }

      onCartCountUpdate(event) {
        if (!this.cartLink) return;

        const count = Number(event.detail?.item_count);
        if (!Number.isFinite(count)) return;

        this.cartLink.setAttribute(
          "aria-label",
          `Cart, ${count} ${count === 1 ? "item" : "items"}`,
        );
      }

      hydrateDesktopMenu(details) {
        const template = details?.querySelector(
          ":scope > template[data-sw-desktop-menu-template]",
        );
        if (!template?.content) return;

        template.replaceWith(template.content.cloneNode(true));
        this.initializeDeviceCategoryControls(details);
      }

      initializeDeviceCategoryControls(scope = this) {
        scope.querySelectorAll("[data-sw-device-category]").forEach((link) => {
          if (link.dataset.swDeviceCategoryReady === "true") return;
          link.dataset.swDeviceCategoryReady = "true";

          link.addEventListener("pointerenter", () => {
            if (this.desktopHoverQuery.matches) {
              this.activateDeviceExplore(link);
            }
          });
          link.addEventListener("focus", () => {
            if (window.innerWidth >= 990) {
              this.activateDeviceExplore(link);
            }
          });
        });
      }

      hydrateMobileMenu() {
        const template = this.mobileMenu?.querySelector(
          ":scope > template[data-sw-mobile-menu-template]",
        );
        if (template?.content) {
          template.replaceWith(template.content.cloneNode(true));
        }
        this.initializeMobileMenuControls();
      }

      initializeMobileMenuControls() {
        if (
          !this.mobileMenu ||
          this.mobileMenu.dataset.swControlsReady === "true" ||
          !this.mobileMenu.querySelector("[data-sw-mobile-panel]")
        ) {
          return;
        }

        this.mobileMenu.dataset.swControlsReady = "true";

        this.mobileMenu
          .querySelectorAll("[data-sw-mobile-forward]")
          .forEach((button) => {
            button.addEventListener("click", () => {
              this.mobileMenu
                .querySelectorAll("[data-sw-mobile-forward][aria-expanded]")
                .forEach((control) =>
                  control.setAttribute(
                    "aria-expanded",
                    control === button ? "true" : "false",
                  ),
                );
              this.showMobilePanel(button.dataset.swMobileForward);
            });
          });

        this.mobileMenu
          .querySelectorAll("[data-sw-mobile-back]")
          .forEach((button) => {
            button.addEventListener("click", () =>
              this.showMobilePanel(button.dataset.swMobileBack),
            );
          });

        this.mobileMenu
          .querySelectorAll("[data-sw-mobile-category-toggle]")
          .forEach((button) => {
            button.addEventListener("click", () =>
              this.toggleMobileCategory(button),
            );
          });
      }

      openMobileMenu() {
        this.hydrateMobileMenu();
        if (!this.mobileMenu) return;

        this.lastFocused = document.activeElement;
        this.closeDesktopMenus();
        this.mobileMenu.hidden = false;
        this.showMobilePanel("root");
        this.openButton?.setAttribute("aria-expanded", "true");
        this.openButton?.setAttribute("aria-label", "Close menu");
        document.body.classList.add("sw-menu-lock");

        requestAnimationFrame(() => {
          this.mobileMenu.classList.add("is-open");
        });
      }

      closeMobileMenu(restoreFocus = true) {
        if (!this.mobileMenu || this.mobileMenu.hidden) return;

        this.mobileMenu.classList.remove("is-open");
        this.openButton?.setAttribute("aria-expanded", "false");
        this.openButton?.setAttribute("aria-label", "Open menu");
        document.body.classList.remove("sw-menu-lock");

        window.setTimeout(() => {
          if (!this.mobileMenu) return;
          this.mobileMenu.hidden = true;
          this.showMobilePanel("root");
          if (restoreFocus && this.lastFocused instanceof HTMLElement)
            this.lastFocused.focus();
        }, 260);
      }

      showMobilePanel(name) {
        if (!this.mobileMenu || !name) return;

        if (name === "root") {
          this.querySelectorAll("[data-sw-mobile-forward][aria-expanded]").forEach(
            (control) => control.setAttribute("aria-expanded", "false"),
          );
          this.collapseMobileCategories();
        }

        const panels = this.mobileMenu.querySelectorAll(
          "[data-sw-mobile-panel]",
        );
        const target = Array.from(panels).find(
          (panel) => panel.dataset.swMobilePanel === name,
        );
        if (!target) return;

        panels.forEach((panel) => panel.classList.remove("is-active"));
        target.classList.add("is-active");
        target.scrollTop = 0;

        const focusTarget =
          name === "root"
            ? target.querySelector(".sw-mobile-menu__category-link")
            : target.querySelector("[data-sw-mobile-back], button, a");
        focusTarget?.focus({ preventScroll: true });
      }

      toggleMobileCategory(button) {
        const submenuId = button.getAttribute("aria-controls");
        const submenu = submenuId
          ? this.ownerDocument.getElementById(submenuId)
          : null;
        if (!submenu || !this.contains(submenu)) return;

        const shouldExpand = button.getAttribute("aria-expanded") !== "true";
        this.collapseMobileCategories(button);
        button.setAttribute("aria-expanded", String(shouldExpand));
        submenu.hidden = !shouldExpand;

        const categoryName = button.dataset.swMobileCategoryName || "category";
        button.setAttribute(
          "aria-label",
          `${shouldExpand ? "Collapse" : "Expand"} ${categoryName}`,
        );
      }

      collapseMobileCategories(exceptButton = null) {
        this.querySelectorAll("[data-sw-mobile-category-toggle]").forEach(
          (button) => {
            if (button === exceptButton) return;

            button.setAttribute("aria-expanded", "false");
            const submenuId = button.getAttribute("aria-controls");
            const submenu = submenuId
              ? this.ownerDocument.getElementById(submenuId)
              : null;
            if (submenu && this.contains(submenu)) submenu.hidden = true;

            const categoryName =
              button.dataset.swMobileCategoryName || "category";
            button.setAttribute("aria-label", `Expand ${categoryName}`);
          },
        );
      }

      trapMobileFocus(event) {
        if (!this.mobileMenu) return;

        const menuFocusable = Array.from(
          this.mobileMenu.querySelectorAll(
            'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
          ),
        );
        const focusable = [this.openButton, ...menuFocusable].filter(
          (element) =>
            element instanceof HTMLElement && element.getClientRects().length > 0,
        );

        if (!focusable.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      initializeDeviceSearch() {
        if (
          !this.searchForm ||
          !this.searchInput ||
          !this.searchResults ||
          !this.deviceSearch
        ) {
          return;
        }

        const signal = this.searchController.signal;

        this.searchForm.addEventListener(
          "submit",
          (event) => {
            event.preventDefault();
            this.runDeviceSearch();
          },
          { signal },
        );

        this.searchInput.addEventListener(
          "input",
          () => this.runDeviceSearch(),
          { signal },
        );

        this.searchInput.addEventListener(
          "focus",
          () => this.deviceSearch.load().catch(() => {}),
          { signal },
        );

        this.searchInput.addEventListener(
          "keydown",
          (event) => {
            if (event.key !== "Escape") return;
            event.stopPropagation();
            this.resetDeviceSearch();
            this.searchInput.focus();
          },
          { signal },
        );

        this.searchClear?.addEventListener(
          "click",
          () => {
            this.resetDeviceSearch();
            this.searchInput.focus();
          },
          { signal },
        );

        this.resetDeviceSearch();
      }

      normalizeSearchText(value) {
        return this.deviceSearch.normalize(value);
      }

      getDeviceSearchShortcut(query) {
        if (!this.searchShortcut) return null;

        const queries = (this.searchShortcut.dataset.searchQueries || "")
          .split("|")
          .map((value) => this.normalizeSearchText(value))
          .filter(Boolean);
        if (!queries.includes(query)) return null;

        const title = this.searchShortcut.dataset.searchTitle?.trim();
        const url = this.searchShortcut.dataset.searchUrl?.trim();
        if (!title || !url) return null;

        return {
          title,
          url,
          brand: "",
          releaseYear: null,
          image: this.searchShortcut.dataset.searchImage || "",
          subtitle: this.searchShortcut.dataset.searchSubtitle?.trim() || "",
          actionLabel:
            this.searchShortcut.dataset.searchActionLabel?.trim() ||
            "View Skins",
        };
      }

      escapeSearchHTML(value) {
        return String(value ?? "").replace(
          /[&<>"']/g,
          (character) =>
            ({
              "&": "&amp;",
              "<": "&lt;",
              ">": "&gt;",
              '"': "&quot;",
              "'": "&#039;",
            })[character],
        );
      }

      getSearchReleaseYear(device) {
        if (
          device.releaseYear === null ||
          device.releaseYear === "" ||
          typeof device.releaseYear === "undefined"
        ) {
          return null;
        }

        const year = Number(device.releaseYear);
        return Number.isInteger(year) && year >= 1900 && year <= 2200
          ? year
          : null;
      }

      resetDeviceSearch() {
        this.searchRequestId += 1;
        if (this.searchInput) this.searchInput.value = "";
        if (this.searchClear) this.searchClear.hidden = true;
        if (this.searchDefault) this.searchDefault.hidden = false;
        if (this.searchResults) {
          this.searchResults.hidden = true;
          this.searchResults.innerHTML = "";
          this.searchResults.setAttribute("aria-busy", "false");
        }
      }

      renderDeviceSearchLoading() {
        if (!this.searchResults) return;
        if (this.searchDefault) this.searchDefault.hidden = true;
        this.searchResults.hidden = false;
        this.searchResults.setAttribute("aria-busy", "true");
        this.searchResults.innerHTML = `
          <div class="sw-search-mega__status">
            <span class="sw-search-mega__spinner" aria-hidden="true"></span>
            <span>Searching all devices…</span>
          </div>
        `;
      }

      renderDeviceSearchError() {
        if (!this.searchResults) return;
        if (this.searchDefault) this.searchDefault.hidden = true;
        this.searchResults.hidden = false;
        this.searchResults.setAttribute("aria-busy", "false");
        this.searchResults.innerHTML = `
          <div class="sw-search-mega__empty" role="status">
            <strong>Search could not load all devices.</strong>
            <span>Please retry or search again.</span>
            <button type="button" data-sw-search-retry>Retry search</button>
          </div>
        `;
        this.searchResults.querySelector("[data-sw-search-retry]")
          ?.addEventListener("click", () => this.runDeviceSearch(), {
            signal: this.searchController.signal,
          });
      }

      renderDeviceSearchResults(matches, searchLabel) {
        if (!this.searchResults) return;

        if (this.searchDefault) this.searchDefault.hidden = true;
        this.searchResults.hidden = false;
        this.searchResults.setAttribute("aria-busy", "false");

        if (!matches.length) {
          this.searchResults.innerHTML = `
            <div class="sw-search-mega__empty">
              <strong>Can’t find your device?</strong>
              <span>Try another model name or request a device.</span>
              <a href="/collections/new-devices#request-a-device">Request a Device</a>
            </div>
          `;
          return;
        }

        const visibleMatches = matches.slice(0, 6);
        const resultRows = visibleMatches
          .map((device) => {
            const title = this.escapeSearchHTML(device.title);
            const url = this.escapeSearchHTML(device.url);
            const brand = this.escapeSearchHTML(device.brand);
            const image = this.escapeSearchHTML(device.image);
            const subtitle = this.escapeSearchHTML(device.subtitle);
            const actionLabel = this.escapeSearchHTML(
              device.actionLabel || "View Skins",
            );
            const releaseYear = this.getSearchReleaseYear(device);
            const meta =
              subtitle ||
              [brand, releaseYear]
                .filter((value) => value !== "" && value !== null)
                .join(" · ");

            return `
              <a class="sw-search-mega__result" href="${url}">
                <span class="sw-search-mega__result-media">
                  ${
                    image
                      ? `<img src="${image}" alt="" loading="lazy" width="52" height="52">`
                      : `<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="7" y="2.5" width="10" height="19" rx="2"></rect><path d="M10 18.5h4"></path></svg>`
                  }
                </span>
                <span class="sw-search-mega__result-copy">
                  <strong>${title}</strong>
                  ${meta ? `<span>${meta}</span>` : ""}
                </span>
                <span class="sw-search-mega__result-action">
                  ${actionLabel}
                  <svg viewBox="0 0 16 16" aria-hidden="true">
                    <path d="M3 8h10M9 4l4 4-4 4"></path>
                  </svg>
                </span>
              </a>
            `;
          })
          .join("");

        const resultSummary =
          matches.length > visibleMatches.length
            ? `Showing ${visibleMatches.length} of ${matches.length} results`
            : `${matches.length} ${
                matches.length === 1 ? "result" : "results"
              }`;

        this.searchResults.innerHTML = `
          <div class="sw-search-mega__results-head">
            <span>${resultSummary} for “${this.escapeSearchHTML(
              searchLabel,
            )}”</span>
          </div>
          <div class="sw-search-mega__result-list">${resultRows}</div>
        `;
      }

      async runDeviceSearch() {
        if (!this.searchInput || !this.searchResults) return;

        const rawQuery = this.searchInput.value.trim();
        const query = this.normalizeSearchText(rawQuery);
        const currentRequestId = ++this.searchRequestId;

        if (this.searchClear) {
          this.searchClear.hidden = !this.searchInput.value.length;
        }

        if (!query.length) {
          this.resetDeviceSearch();
          return;
        }

        if (this.searchDefault) this.searchDefault.hidden = true;

        if (query.length < 2) {
          this.searchResults.hidden = false;
          this.searchResults.setAttribute("aria-busy", "false");
          this.searchResults.innerHTML = `
            <div class="sw-search-mega__status">
              Keep typing to search your device.
            </div>
          `;
          return;
        }

        const shortcut = this.getDeviceSearchShortcut(query);
        if (shortcut) {
          this.renderDeviceSearchResults([shortcut], rawQuery);
          return;
        }

        if (!this.deviceSearch.isComplete()) {
          this.renderDeviceSearchLoading();
        }

        try {
          await this.deviceSearch.load();
        } catch (error) {
          if (
            !this.searchController.signal.aborted &&
            currentRequestId === this.searchRequestId &&
            this.normalizeSearchText(this.searchInput.value) === query
          ) this.renderDeviceSearchError();
          return;
        }

        if (
          this.searchController.signal.aborted ||
          currentRequestId !== this.searchRequestId ||
          this.normalizeSearchText(this.searchInput.value) !== query
        ) return;

        this.renderDeviceSearchResults(this.deviceSearch.search(query), rawQuery);
      }

      activateDeviceExplore(link) {
        const menu = link.closest(".sw-device-zones");
        const targetId = link.dataset.swExploreTarget;
        if (!menu || !targetId) return;

        const target = menu.querySelector(`#${CSS.escape(targetId)}`);
        if (!target) return;

        menu.querySelectorAll("[data-sw-device-category]").forEach((control) => {
          const isActive = control === link;
          control.classList.toggle("is-active", isActive);
          control.closest("li")?.classList.toggle("is-active", isActive);
        });

        menu
          .querySelectorAll("[data-sw-device-explore-panel]")
          .forEach((panel) => {
            const isActive = panel === target;
            panel.hidden = !isActive;
            panel.classList.toggle("is-active", isActive);
          });
      }

      openDesktopMenu(details) {
        this.hydrateDesktopMenu(details);
        this.clearDesktopOpen(details);
        this.clearDesktopClose(details);
        this.desktopDetails.forEach((other) => {
          if (other !== details) {
            this.clearDesktopOpen(other);
            this.clearDesktopClose(other);
            other.removeAttribute("open");
            other.dataset.swDesktopClickArmed = "false";
          }
        });
        details.setAttribute("open", "");
      }

      queueDesktopOpen(details) {
        if (details.open) return;
        this.clearDesktopOpen(details);
        const timer = window.setTimeout(() => {
          this.openDesktopMenu(details);
          this.desktopOpenTimers.delete(details);
        }, 300);
        this.desktopOpenTimers.set(details, timer);
      }

      queueDesktopClose(details) {
        this.clearDesktopClose(details);
        const timer = window.setTimeout(() => {
          details.removeAttribute("open");
          details.dataset.swDesktopClickArmed = "false";
          this.desktopCloseTimers.delete(details);
        }, 400);
        this.desktopCloseTimers.set(details, timer);
      }

      clearDesktopOpen(details) {
        const timer = this.desktopOpenTimers.get(details);
        if (timer) window.clearTimeout(timer);
        this.desktopOpenTimers.delete(details);
      }

      clearDesktopClose(details) {
        const timer = this.desktopCloseTimers.get(details);
        if (timer) window.clearTimeout(timer);
        this.desktopCloseTimers.delete(details);
      }

      closeDesktopMenus() {
        this.desktopDetails.forEach((details) => {
          this.clearDesktopOpen(details);
          this.clearDesktopClose(details);
          details.removeAttribute("open");
          details.dataset.swDesktopClickArmed = "false";
        });
      }
    },
  );
}
